import React from 'react';
import { Field as FinalFormField } from 'react-final-form';
import { Accordion as MuiAccordion, Chip } from '@material-ui/core';

// DEBUG ONLY - shows a small "render count" chip on every enquiry-form field and accordion so you can see
// how many times each one re-renders while you type.
//
// OFF by default, so there is nothing to switch off before pushing. To turn it on locally:
//     localStorage.setItem('rvRenderCount', 'true')   (browser console, then reload)
// and to turn it off again:
//     localStorage.removeItem('rvRenderCount')        (then reload)
//
// Cost when off: none. `Field` and `createRenderCountAccordion(...)` hand back the original react-final-form
// Field / MUI Accordion themselves (no wrapper, no extra element, no hook), decided once at module load.
// Production builds never enable it (NODE_ENV check), so the debug branch is also dropped from the bundle.
const readStorageFlag = (): boolean => {
    try {
        return window.localStorage.getItem('rvRenderCount') === 'true';
    } catch {
        return false;
    }
};

export const IS_RENDER_COUNT_ENABLED: boolean = process.env.NODE_ENV !== 'production' && readStorageFlag();

export const RenderCountBadge: React.FC<{ label: string }> = ({ label }) => {
    const renders = React.useRef(0);
    renders.current += 1;

    return (
        <Chip
            size="small"
            label={`${label} · ${renders.current}`}
            style={{
                position: 'absolute',
                top: -8,
                right: 4,
                zIndex: 1400,
                height: 16,
                fontSize: 10,
                pointerEvents: 'none',
                color: '#fff',
                backgroundColor: renders.current > 10 ? '#d32f2f' : renders.current > 3 ? '#ed6c02' : '#2e7d32',
            }}
        />
    );
};

// tslint:disable-next-line: no-any
const DebugFieldBody: React.FC<{ fieldName: string; renderField: (fieldProps: any) => React.ReactNode; fieldProps: any }> = (
    { fieldName, renderField, fieldProps },
) => (
    <div style={{ position: 'relative' }}>
        {renderField(fieldProps)}
        <RenderCountBadge label={fieldName} />
    </div>
);

// Counts how many times the Field's render function runs (children are a new function on every parent render,
// so DebugFieldBody re-renders exactly when the Field does).
// tslint:disable-next-line: no-any
const DebugField = (fieldProps: any) => {
    const { children, ...restProps } = fieldProps;
    if (typeof children !== 'function') {
        return <FinalFormField {...fieldProps} />;
    }
    return (
        <FinalFormField {...restProps}>
            {/* tslint:disable-next-line: no-any */}
            {(renderProps: any) => <DebugFieldBody fieldName={String(fieldProps.name)} renderField={children} fieldProps={renderProps} />}
        </FinalFormField>
    );
};

export const Field = (IS_RENDER_COUNT_ENABLED ? DebugField : FinalFormField) as typeof FinalFormField;

// tslint:disable-next-line: no-any
export const createRenderCountAccordion = (name: string): typeof MuiAccordion => {
    if (!IS_RENDER_COUNT_ENABLED) {
        return MuiAccordion;
    }
    // tslint:disable-next-line: no-any
    const DebugAccordion = (accordionProps: any) => (
        <div style={{ position: 'relative' }}>
            <MuiAccordion {...accordionProps} />
            <RenderCountBadge label={name} />
        </div>
    );
    return DebugAccordion as unknown as typeof MuiAccordion;
};
